/* A server agent's runs, and one run's trace — `backend/modules/runs/README.md` ("Its trace")
   and `backend/modules/event_socket/README.md` ("Watching a run's trace").

   A trace is read once over REST, and while its run is live the rest arrives on
   the events socket (`watchTrace`) and is merged into the same query's data, so
   the screen has one source whether a step came from the read or the watch. In
   mock mode there is no socket: a live trace is re-read instead. */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

import { client } from "./client";
import { unwrap, type Envelope } from "./envelope";
import { watchTrace } from "./socket";

import type { ApiRun, ApiSpan, ApiTrace, TraceRecord } from "@/shared/types";

export const traceKeys = {
  all: ["traces"] as const,
  runs: (agentId: string) => [...traceKeys.all, "runs", agentId] as const,
  one: (runId: string) => [...traceKeys.all, "one", runId] as const,
};

/** The API's page ceiling for an agent's runs. */
const RUN_LIMIT = 200;
/* Runs also start where no event reaches this device — a channel, a webhook —
   so an open list re-reads itself, as the threads list does. */
const LIST_REFRESH_MS = 30_000;
const MOCK_POLL_MS = 1_000;
const SOCKET = process.env.REACT_APP_API_MOCK !== "true";

/* ------------------------------ endpoints -------------------------------- */

/** Newest first. */
export async function listAgentRuns(agentId: string): Promise<ApiRun[]> {
  return unwrap(await client.get<Envelope<ApiRun[]>>("/runs", { params: { agent_id: agentId, limit: RUN_LIMIT } }));
}

export async function getRunTrace(runId: string): Promise<ApiTrace> {
  return unwrap(await client.get<Envelope<ApiTrace>>(`/runs/${runId}/trace`));
}

/* ------------------------------- merging --------------------------------- */

const BLANK: Omit<ApiSpan, "id"> = {
  parent_span_id: null, obs_type: "SPAN", kind: "llm", name: null, status: "pending", seq: null,
  started_at: new Date(0).toISOString(), duration_ms: null, ttft_ms: null, model: null, tool: null,
  input: null, output: null, tokens_in: null, tokens_out: null, tokens_cached: null, cost_usd: null,
};

/** One watched record into the trace: its part merged into the span of its id,
    later keys winning; a span not seen yet joins the end. `cursor` is where the
    record was, so a later read or watch picks up after it. */
export function applyRecord(trace: ApiTrace, record: TraceRecord, cursor: string): ApiTrace {
  if (!("span" in record)) return { ...trace, cursor };
  const part = record.span;
  const known = trace.spans.some((span) => span.id === part.id);
  const spans = known
    ? trace.spans.map((span) => (span.id === part.id ? { ...span, ...part } : span))
    : [...trace.spans, { ...BLANK, ...part }];
  return { ...trace, spans, cursor };
}

/* -------------------------------- hooks ---------------------------------- */

/** An agent's runs. Off (no request) for a null id. */
export const useAgentRuns = (agentId: string | null) =>
  useQuery({
    queryKey: traceKeys.runs(agentId ?? ""),
    queryFn: () => listAgentRuns(agentId!),
    enabled: !!agentId,
    refetchInterval: LIST_REFRESH_MS,
  });

/** One run's trace; while `live`, kept current from the socket. Its end
    re-reads the runs and the trace, which by then come from where they are kept. */
export function useRunTrace(runId: string | null, live: boolean) {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: traceKeys.one(runId ?? ""),
    queryFn: () => getRunTrace(runId!),
    enabled: !!runId,
    refetchInterval: live && !SOCKET ? MOCK_POLL_MS : false,
  });
  const loaded = query.isSuccess;
  useEffect(() => {
    if (!runId || !live || !loaded || !SOCKET) return;
    const key = traceKeys.one(runId);
    // Read here, not a dependency: each record moves it, and restarting the watch
    // on every record would only ask the server again for what it is sending.
    const since = qc.getQueryData<ApiTrace>(key)?.cursor ?? "";
    return watchTrace(runId, since, (record, cursor) => {
      qc.setQueryData<ApiTrace>(key, (trace) => trace && applyRecord(trace, record, cursor));
      if ("end" in record) void qc.invalidateQueries({ queryKey: traceKeys.all });
    });
  }, [qc, runId, live, loaded]);
  return query;
}
